/**
 * Part Pricing History Report (20.4) — table, multi-vendor price line chart.
 */

import { useCallback, useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { fetchPartPricingHistoryReport, exportPartPricingHistoryCSV, exportPartPricingHistoryPDF } from '../../api/reports';
import { LoadingSpinner, EmptyState } from '../common';
import ReportShell, { DateRangeFilter } from './ReportShell';

const VENDOR_COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

function fmtCurrency(v) {
  if (v == null) return '—';
  return `$${parseFloat(v).toFixed(2)}`;
}

export default function PartPricingHistoryReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [partFilter, setPartFilter] = useState('');
  const [exporting, setExporting] = useState(null);

  const params = {
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    part_id: partFilter || undefined,
  };

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await fetchPartPricingHistoryReport(params)); }
    catch { setData(null); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo, partFilter]);

  useEffect(() => { load(); }, [load]);

  async function handleExport(type) {
    setExporting(type);
    try {
      if (type === 'csv') await exportPartPricingHistoryCSV(params);
      else await exportPartPricingHistoryPDF(params);
    } catch {} finally { setExporting(null); }
  }

  const rows = data?.rows || [];

  // Build chart data — group by date with vendor as separate lines
  const vendors = [...new Set(rows.map((r) => r.vendor_name))];
  const dateMap = {};
  rows.forEach((r) => {
    const date = r.quote_received_date?.slice(0, 10);
    if (!date) return;
    if (!dateMap[date]) dateMap[date] = { date };
    dateMap[date][r.vendor_name] = parseFloat(r.quote_unit_price);
  });
  const chartData = Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date));

  return (
    <ReportShell
      title="Part Pricing History"
      exporting={exporting}
      onExportCSV={() => handleExport('csv')}
      onExportPDF={() => handleExport('pdf')}
      filters={
        <>
          <DateRangeFilter dateFrom={dateFrom} dateTo={dateTo} onChangeFrom={setDateFrom} onChangeTo={setDateTo} />
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Part ID</label>
            <input
              type="text"
              className="border border-gray-300 rounded-md px-2 py-1 text-sm w-48"
              placeholder="Filter by part..."
              value={partFilter}
              onChange={(e) => setPartFilter(e.target.value)}
            />
          </div>
          <button onClick={load} className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 self-end">Run</button>
        </>
      }
    >
      {loading ? <div className="flex justify-center py-10"><LoadingSpinner /></div> : rows.length === 0 ? (
        <EmptyState title="No pricing history" description="Quote data will appear after vendor quotes are received." />
      ) : (
        <>
          <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
                  <th className="px-3 py-2">Part #</th>
                  <th className="px-3 py-2">Vendor</th>
                  <th className="px-3 py-2">Vendor PN</th>
                  <th className="px-3 py-2 text-right">Unit Price</th>
                  <th className="px-3 py-2 text-right">Change</th>
                  <th className="px-3 py-2 text-right">Lead Time</th>
                  <th className="px-3 py-2">Source</th>
                  <th className="px-3 py-2">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((r, i) => (
                  <tr key={r.quote_history_id || i} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-mono text-xs">{r.part_number}</td>
                    <td className="px-3 py-2 text-foreground">{r.vendor_name}</td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-500">{r.vendor_part_number}</td>
                    <td className="px-3 py-2 text-right font-medium">{fmtCurrency(r.quote_unit_price)}</td>
                    <td className="px-3 py-2 text-right">
                      {r.price_change_pct != null && (
                        <span className={parseFloat(r.price_change_pct) > 0 ? 'text-red-600' : 'text-green-600'}>
                          {parseFloat(r.price_change_pct) > 0 ? '+' : ''}{r.price_change_pct}%
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-500">
                      {r.quoted_lead_time_days != null ? `${r.quoted_lead_time_days}d` : '—'}
                    </td>
                    <td className="px-3 py-2 text-gray-400 text-xs">{r.quote_source || '—'}</td>
                    <td className="px-3 py-2 text-gray-400 text-xs">{r.quote_received_date?.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {chartData.length > 1 && vendors.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="text-sm font-medium text-foreground mb-3">Price Over Time</h3>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  {vendors.slice(0, 8).map((v, i) => (
                    <Line key={v} type="monotone" dataKey={v} stroke={VENDOR_COLORS[i % VENDOR_COLORS.length]} strokeWidth={2} dot={false} connectNulls />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}
    </ReportShell>
  );
}
